import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { getEnvVariable } from '@appcharge/shared-ui';
import axios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';

import { ELocalStorageKeys } from '../constants/enums';
import { AuthSliceState } from '../store/store.types';
import { uiActions } from '../store/uiSlice';
import { localStorageUtil } from '../utils/localStorageUtil';

import { useLogin } from './useLogin';

export const baseURLPromise = async (): Promise<string> => {
  let baseUrl = '';
  if (getEnvVariable('REACT_APP_ENV') !== 'ephemeral') {
    baseUrl = getEnvVariable('REACT_APP_API_BASE_URL') || '';
  } else {
    const response = await fetch('/base.json');
    const data = await response.json();
    baseUrl = data.apiBaseUrl;
  }

  axios.defaults.baseURL = baseUrl;
  return baseUrl;
};

// axios.defaults.baseURL =
//   localStorageUtil.getAny(ELocalStorageKeys.ENVIRONMENT) ||
//   getEnvVariable('REACT_APP_API_BASE_URL');
axios.defaults.withCredentials = true;

export default function useAxios() {
  const { cleanUserLoginData } = useLogin();
  const dispatch = useDispatch();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const publisherId =
    useParams().publisherId ?? searchParams.get('publisherId');

  const authToken =
    useSelector(({ auth }: { auth: AuthSliceState }) => auth.authJwt) ||
    localStorageUtil.get(ELocalStorageKeys.JWT_TOKEN) ||
    localStorageUtil.get(ELocalStorageKeys.JWT_TOKEN, true);
  const sessionId = localStorageUtil.get(ELocalStorageKeys.SESSION_ID);

  const headersData: AxiosRequestHeaders | undefined = {
    ...(authToken && { 'x-access-token': authToken }),
    ...(publisherId && { 'x-publishers': publisherId }),
    ...(sessionId && { 'x-session-id': sessionId })
  };

  const post = async (
    path: string,
    data: any,
    customHeaders?: AxiosRequestHeaders
  ) => {
    await baseURLPromise();

    dispatch(uiActions.setGlobalLoading(true));
    return await axios({
      url: path,
      method: 'POST',
      headers: customHeaders ?? headersData,
      data
    })
      .then((res) => {
        dispatch(uiActions.setUnsavedChanges(false));
        return res;
      })
      .catch((err) => {
        if (err.response.status === 401) {
          cleanUserLoginData();
        }
        return Promise.reject(err);
      })
      .finally(() => {
        dispatch(uiActions.setGlobalLoading(false));
      });
  };

  const get = async (
    path: string,
    params?: any,
    customHeaders?: AxiosRequestHeaders
  ) => {
    await baseURLPromise();
    const response = await axios({
      url: path,
      method: 'GET',
      headers: customHeaders ?? headersData,
      params: params || undefined
    }).catch((err) => {
      if (err.response.status === 401) {
        cleanUserLoginData();
      }
      return Promise.reject(err);
    });
    return response!.data;
  };

  const put = async (
    path: string,
    data: any,
    customHeaders?: AxiosRequestHeaders,
    onUploadProgress?: AxiosRequestConfig['onUploadProgress']
  ) => {
    await baseURLPromise();
    dispatch(uiActions.setGlobalLoading(true));
    const response = await axios({
      url: path,
      method: 'PUT',
      headers: customHeaders ?? headersData,
      onUploadProgress,
      data
    })
      .catch((err) => {
        if (err.response.status === 401) {
          cleanUserLoginData();
        }
        return Promise.reject(err);
      })
      .finally(() => {
        dispatch(uiActions.setGlobalLoading(false));
      });
    return response!.data;
  };

  const del = async (
    path: string,
    data?: any,
    customHeaders?: AxiosRequestHeaders
  ) => {
    await baseURLPromise();
    dispatch(uiActions.setGlobalLoading(true));
    const config: {
      url: string;
      method: string;
      headers: AxiosRequestHeaders | undefined;
      data?: any;
    } = {
      url: path,
      method: 'DELETE',
      headers: customHeaders ?? headersData,
      data
    };
    if (data) {
      config.data = data;
    }
    const response = await axios(config)
      .catch((err) => {
        if (err.response.status === 401) {
          cleanUserLoginData();
        }
        return Promise.reject(err);
      })
      .finally(() => {
        dispatch(uiActions.setGlobalLoading(false));
      });
    return response!.data;
  };

  const patch = async (path: string, data: any, customHeaders?: AxiosRequestHeaders) => {
    await baseURLPromise();
    dispatch(uiActions.setGlobalLoading(true));
    const response = await axios({
      url: path,
      method: 'PATCH',
      headers: customHeaders ?? headersData,
      data
    })
      .then((res) => {
        return res;
      })
      .catch((err) => {
        if (err.response && err.response.status === 401) {
          cleanUserLoginData();
        }
        return Promise.reject(err);
      })
      .finally(() => {
        dispatch(uiActions.setGlobalLoading(false));
      });
    return response!.data;
  };

  return { post, get, put, del, patch };
}
